import React, { useRef, useState, useCallback, useEffect } from 'react';

const NODES = [
  { id: 'auth',    label: 'Auth Service',    x: 60,  y: 80  },
  { id: 'api',     label: 'API Gateway',     x: 240, y: 40  },
  { id: 'db',      label: 'Database',        x: 420, y: 80  },
  { id: 'cache',   label: 'Cache',           x: 240, y: 160 },
  { id: 'worker',  label: 'Worker',          x: 420, y: 200 },
];

const REQUIRED_CONNECTIONS = [
  { from: 'auth',   to: 'api',    label: 'Auth → API Gateway' },
  { from: 'api',    to: 'db',     label: 'API Gateway → Database' },
  { from: 'api',    to: 'cache',  label: 'API Gateway → Cache' },
  { from: 'cache',  to: 'worker', label: 'Cache → Worker' },
];

const NODE_R = 32;
const SVG_W = 520;
const SVG_H = 260;

function connKey(a, b) { return `${a}--${b}`; }

export default function C22_DragConnect({ onComplete }) {
  const svgRef = useRef(null);
  const [connections, setConnections] = useState([]);
  const [dragging, setDragging] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [verified, setVerified] = useState(false);

  const getSVGPos = (clientX, clientY) => {
    const rect = svgRef.current.getBoundingClientRect();
    const scaleX = SVG_W / rect.width;
    const scaleY = SVG_H / rect.height;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const getNodeAt = (x, y) => {
    return NODES.find(n => Math.hypot(n.x - x, n.y - y) <= NODE_R);
  };

  const onMouseDown = useCallback((e, nodeId) => {
    if (verified) return;
    e.preventDefault();
    setDragging(nodeId);
    const pos = getSVGPos(e.clientX, e.clientY);
    setMousePos(pos);
  }, [verified]);

  const onMouseMove = useCallback((e) => {
    if (!dragging) return;
    e.preventDefault();
    const pos = getSVGPos(e.clientX, e.clientY);
    setMousePos(pos);
  }, [dragging]);

  const onMouseUp = useCallback((e) => {
    if (!dragging) return;
    const pos = getSVGPos(e.clientX, e.clientY);
    const target = getNodeAt(pos.x, pos.y);
    if (target && target.id !== dragging) {
      const key = connKey(dragging, target.id);
      const keyRev = connKey(target.id, dragging);
      setConnections(prev => {
        if (prev.includes(key) || prev.includes(keyRev)) {
          return prev.filter(k => k !== key && k !== keyRev);
        }
        return [...prev, key];
      });
    }
    setDragging(null);
  }, [dragging]);

  const handleVerify = () => {
    setVerified(true);
    let correct = 0;
    const details = {};
    REQUIRED_CONNECTIONS.forEach(req => {
      const key = connKey(req.from, req.to);
      const keyRev = connKey(req.to, req.from);
      const pass = connections.includes(key) || connections.includes(keyRev);
      if (pass) correct++;
      details[key] = { label: req.label, pass };
    });
    const score = Math.round((correct / REQUIRED_CONNECTIONS.length) * 100);
    onComplete({ score, details, passed: score === 100 });
  };

  const drawnConnections = connections.map(key => {
    const [aId, bId] = key.split('--');
    const a = NODES.find(n => n.id === aId);
    const b = NODES.find(n => n.id === bId);
    return { key, a, b };
  }).filter(c => c.a && c.b);

  const dragSourceNode = dragging ? NODES.find(n => n.id === dragging) : null;

  return (
    <div className="ab-challenge-ui">
      <p className="ab-hint">
        Connect the architecture nodes by dragging from one node to another. Click a connection again to remove it.
        Required connections: Auth→API, API→DB, API→Cache, Cache→Worker.
      </p>

      <svg
        ref={svgRef}
        width={SVG_W}
        height={SVG_H}
        style={{ width: '100%', maxWidth: SVG_W, display: 'block', background: 'var(--surface)', borderRadius: 10, border: '1.5px solid var(--border2)', cursor: dragging ? 'crosshair' : 'default', userSelect: 'none' }}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
      >
        {drawnConnections.map(({ key, a, b }) => {
          const isRequired = REQUIRED_CONNECTIONS.some(r =>
            (r.from === key.split('--')[0] && r.to === key.split('--')[1]) ||
            (r.to === key.split('--')[0] && r.from === key.split('--')[1])
          );
          return (
            <line key={key}
              x1={a.x} y1={a.y} x2={b.x} y2={b.y}
              stroke={verified ? (isRequired ? 'var(--green)' : 'var(--red)') : 'var(--accent)'}
              strokeWidth={2.5}
              strokeDasharray={verified && !isRequired ? '5,4' : undefined}
              markerEnd="url(#arrow)"
            />
          );
        })}

        {dragSourceNode && (
          <line
            x1={dragSourceNode.x} y1={dragSourceNode.y}
            x2={mousePos.x} y2={mousePos.y}
            stroke="#f9a825" strokeWidth={2} strokeDasharray="6,4" opacity={0.8}
          />
        )}

        <defs>
          <marker id="arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
            <path d="M0,0 L0,6 L8,3 z" fill="var(--accent)" />
          </marker>
        </defs>

        {NODES.map(node => {
          const isDragging = dragging === node.id;
          return (
            <g key={node.id} style={{ cursor: verified ? 'default' : 'grab' }}
              onMouseDown={(e) => onMouseDown(e, node.id)}>
              <circle cx={node.x} cy={node.y} r={NODE_R}
                fill={isDragging ? 'var(--card2)' : 'var(--card)'}
                stroke={isDragging ? '#f9a825' : 'var(--accent)'}
                strokeWidth={isDragging ? 2.5 : 1.8}
              />
              <text x={node.x} y={node.y - 4} textAnchor="middle" fill="var(--text)" fontSize="10" fontWeight="600">
                {node.label.split(' ')[0]}
              </text>
              <text x={node.x} y={node.y + 9} textAnchor="middle" fill="var(--text2)" fontSize="9">
                {node.label.split(' ').slice(1).join(' ')}
              </text>
            </g>
          );
        })}
      </svg>

      <div style={{ marginTop: 10, fontSize: 12, opacity: 0.7, color: 'var(--text2)' }}>
        Connected: {connections.length} link{connections.length !== 1 ? 's' : ''} · Required: {REQUIRED_CONNECTIONS.length}
      </div>

      {!verified
        ? <button className="ab-action-btn" style={{ marginTop: 12 }} onClick={handleVerify}>Verify Connections</button>
        : <div className="ab-submitted-badge">✓ Connections Verified — Evaluating...</div>
      }
    </div>
  );
}
